import { useEffect, useMemo, useRef, useState } from 'react';
import ValidatedInput from '../../validations/ValidatedInput';
import { productApi, categoryApi, labelApi, sizeChartApi } from './api';
import './ProductsScreen.css';

const EMPTY = {
  title: '', subtitle: '', description: '', category: '', subCategory: '', labels: [],
  sizesAvailable: '', actualPrice: '', discount: '0', fabricType: '', embroidery: '',
  fit: '', careTip: '', remarks: '', noOfComponents: '', colour: '', materialFabric: '',
  isDeal: false, dealBadge: '', sizeChart: '',
};

function sizesFromChart(chart) {
  if (!chart?.rows?.length) return [];
  return chart.rows
    .map((row) => String(row.size || '').trim())
    .filter(Boolean);
}

function formatSizesList(sizes) {
  return sizes.length ? sizes.join(', ') : '';
}

function FileDrop({ files, onChange, existingImages = [] }) {
  const inputRef = useRef(null);
  const [previews, setPreviews] = useState([]);
  const list = files ? [...files] : [];

  useEffect(() => {
    const next = list.map((file) => ({
      key: `${file.name}-${file.size}-${file.lastModified}`,
      url: URL.createObjectURL(file),
      name: file.name,
    }));
    setPreviews(next);
    return () => next.forEach((item) => URL.revokeObjectURL(item.url));
  }, [files]);

  const hasPreviews = previews.length > 0 || existingImages.length > 0;

  return (
    <div className="pr-media">
      <button
        type="button"
        className={`pr-drop${list.length ? ' is-filled' : ''}`}
        onClick={() => inputRef.current?.click()}
      >
        <span className="pr-drop__label">Product images</span>
        <span className="pr-drop__icon" aria-hidden>↑</span>
        <span className="pr-drop__meta">
          {list.length ? `${list.length} new file(s) selected` : '5–7 preferred, max 7'}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => onChange(e.target.files)}
        />
      </button>

      {hasPreviews && (
        <div className="pr-media__grid" aria-label="Image previews">
          {existingImages.map((url, idx) => (
            <figure key={`saved-${url}-${idx}`} className="pr-media__thumb pr-media__thumb--saved">
              <img src={url} alt={`Saved product ${idx + 1}`} />
              <figcaption>Saved</figcaption>
            </figure>
          ))}
          {previews.map((item) => (
            <figure key={item.key} className="pr-media__thumb pr-media__thumb--new">
              <img src={item.url} alt={item.name} />
              <figcaption>New</figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductsScreen({ onMessage }) {
  const flash = (t) => onMessage?.(t);
  const [products, setProducts] = useState([]);
  const [mains, setMains] = useState([]);
  const [subs, setSubs] = useState([]);
  const [labels, setLabels] = useState([]);
  const [charts, setCharts] = useState([]);
  const [productFiles, setProductFiles] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY);

  const filteredSubs = useMemo(
    () => subs.filter((s) => !productForm.category || String(s.parent?._id || s.parent) === productForm.category),
    [subs, productForm.category]
  );

  const activeProducts = useMemo(
    () => products.filter((p) => p.isActive !== false),
    [products]
  );

  const inactiveProducts = useMemo(
    () => products.filter((p) => p.isActive === false),
    [products]
  );

  const refresh = async () => {
    const [p, m, s, l, c] = await Promise.all([
      productApi.adminAll().catch(() => ({ data: { data: [] } })),
      categoryApi.list({ type: 'main', active: 'false' }).catch(() => ({ data: { data: [] } })),
      categoryApi.list({ type: 'sub', active: 'false' }).catch(() => ({ data: { data: [] } })),
      labelApi.list().catch(() => ({ data: { data: [] } })),
      sizeChartApi.list().catch(() => ({ data: { data: [] } })),
    ]);
    setProducts(p.data.data || []);
    setMains(m.data.data || []);
    setSubs(s.data.data || []);
    setLabels(l.data.data || []);
    setCharts(c.data.data || []);
  };

  useEffect(() => {
    refresh().catch(() => flash('Could not load products'));
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const onSizeChartChange = (e) => {
    const chartId = e.target.value;
    const chart = charts.find((c) => c._id === chartId);
    const sizes = sizesFromChart(chart);
    setProductForm((f) => ({
      ...f,
      sizeChart: chartId,
      sizesAvailable: formatSizesList(sizes),
    }));
  };

  const toggleLabel = (id) => {
    setProductForm((f) => ({
      ...f,
      labels: f.labels.includes(id) ? f.labels.filter((x) => x !== id) : [...f.labels, id],
    }));
  };

  const createProduct = async (e) => {
    e.preventDefault();
    if (!editingId && !productFiles?.length) {
      flash('Please add at least one product image');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(productForm).forEach(([k, v]) => {
        if (k === 'labels') fd.append('labels', JSON.stringify(v));
        else if (k === 'sizesAvailable') return;
        else if (k === 'isDeal') fd.append('isDeal', v ? 'true' : 'false');
        else fd.append(k, v ?? '');
      });
      if (productFiles?.length) {
        [...productFiles].forEach((file) => fd.append('images', file));
      }
      if (editingId) {
        await productApi.update(editingId, fd);
        flash('Product updated');
      } else {
        await productApi.create(fd);
        flash('Product created with auto NYVM code');
      }
      setProductForm(EMPTY);
      setProductFiles(null);
      setExistingImages([]);
      setEditingId(null);
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || (editingId ? 'Product update failed' : 'Product create failed'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    const chartId = String(p.sizeChart?._id || p.sizeChart || '');
    const chart = charts.find((c) => c._id === chartId);
    const sizes = chart ? sizesFromChart(chart) : p.sizesAvailable || [];
    setEditingId(p._id);
    setProductForm({
      title: p.title || '',
      subtitle: p.subtitle || '',
      description: p.description || '',
      category: String(p.category?._id || p.category || ''),
      subCategory: String(p.subCategory?._id || p.subCategory || ''),
      labels: (p.labels || []).map((l) => l._id || l),
      sizesAvailable: formatSizesList(sizes),
      actualPrice: String(p.actualPrice ?? ''),
      discount: String(p.discount ?? '0'),
      fabricType: p.fabricType || '',
      embroidery: p.embroidery || '',
      fit: p.fit || '',
      careTip: p.careTip || '',
      remarks: p.remarks || '',
      noOfComponents: p.noOfComponents || '',
      colour: p.colour || '',
      materialFabric: p.materialFabric || '',
      isDeal: Boolean(p.isDeal),
      dealBadge: p.dealBadge || '',
      sizeChart: String(p.sizeChart?._id || p.sizeChart || ''),
    });
    setProductFiles(null);
    setExistingImages(p.images || []);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setProductForm(EMPTY);
    setProductFiles(null);
    setExistingImages([]);
  };

  const renderProductCard = (p, { inactive = false } = {}) => (
    <article
      key={p._id}
      className={`pr-card${editingId === p._id ? ' is-editing' : ''}${inactive ? ' is-inactive' : ''}`}
    >
      <div
        className="pr-card__media"
        style={p.images?.[0] ? { backgroundImage: `url(${p.images[0]})` } : undefined}
      />
      <div className="pr-card__body">
        <code>{p.productCode}</code>
        <strong>{p.title}</strong>
        <span>
          ₹{p.sellingPrice}
          {p.isDeal ? ' · Deal' : ''}
          {inactive ? ' · Hidden' : ''}
        </span>
      </div>
      <div className="pr-card__actions">
        <button type="button" className="pr-card__edit" onClick={() => startEdit(p)}>
          Edit
        </button>
        {inactive ? (
          <button
            type="button"
            className="pr-card__activate"
            onClick={async () => {
              await productApi.activate(p._id);
              flash('Product activated');
              refresh();
            }}
          >
            Activate
          </button>
        ) : (
          <button
            type="button"
            className="pr-card__deactivate"
            onClick={async () => {
              await productApi.deactivate(p._id);
              flash('Product deactivated');
              if (editingId === p._id) cancelEdit();
              refresh();
            }}
          >
            Deactivate
          </button>
        )}
        <button
          type="button"
          className="pr-card__remove"
          onClick={async () => {
            if (!window.confirm(`Permanently delete "${p.title}"? This cannot be undone.`)) return;
            await productApi.remove(p._id);
            flash('Product deleted');
            if (editingId === p._id) cancelEdit();
            refresh();
          }}
        >
          Delete
        </button>
      </div>
    </article>
  );

  return (
    <div className="pr-screen">
      <form className="pr-screen__form" onSubmit={createProduct}>
        <section className="pr-block">
          <p className="pr-screen__label">Basics</p>
          <div className="pr-grid">
            <label className="pr-field pr-field--wide"><span>Title</span>
              <ValidatedInput kind="text" name="title" value={productForm.title} onChange={onChange} required />
            </label>
            <label className="pr-field pr-field--wide"><span>Subtitle</span>
              <ValidatedInput kind="text" name="subtitle" value={productForm.subtitle} onChange={onChange} />
            </label>
            <label className="pr-field"><span>Main category</span>
              <select name="category" value={productForm.category} onChange={onChange} required>
                <option value="">Select</option>
                {mains.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </label>
            <label className="pr-field"><span>Sub category</span>
              <select name="subCategory" value={productForm.subCategory} onChange={onChange} required>
                <option value="">Select</option>
                {filteredSubs.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </label>
            <label className="pr-field pr-field--wide"><span>Description</span>
              <ValidatedInput kind="textarea" name="description" rows={3} value={productForm.description} onChange={onChange} />
            </label>
          </div>
        </section>

        <section className="pr-block">
          <p className="pr-screen__label">Pricing & sizes</p>
          <div className="pr-grid">
            <label className="pr-field"><span>Actual price</span>
              <ValidatedInput kind="number" name="actualPrice" value={productForm.actualPrice} onChange={onChange} required />
            </label>
            <label className="pr-field"><span>Discount %</span>
              <ValidatedInput kind="number" name="discount" value={productForm.discount} onChange={onChange} />
            </label>
            <label className="pr-field"><span>Size chart</span>
              <select name="sizeChart" value={productForm.sizeChart} onChange={onSizeChartChange}>
                <option value="">None</option>
                {charts.map((c) => <option key={c._id} value={c._id}>{c.name}{c.isDefault ? ' (default)' : ''}</option>)}
              </select>
            </label>
            <label className="pr-field"><span>Sizes</span>
              <input
                readOnly
                className="pr-field__readonly"
                value={productForm.sizesAvailable || 'Select a size chart'}
                tabIndex={-1}
                aria-readonly="true"
              />
            </label>
            <label className="pr-field"><span>Deal badge</span>
              <ValidatedInput kind="text" name="dealBadge" value={productForm.dealBadge} onChange={onChange} placeholder="e.g. 30% OFF" />
            </label>
            <label className={`pr-switch${productForm.isDeal ? ' is-on' : ''}`}>
              <input name="isDeal" type="checkbox" checked={productForm.isDeal} onChange={onChange} />
              <span className="pr-switch__track" aria-hidden />
              <span>Mark as deal</span>
            </label>
          </div>
        </section>

        <section className="pr-block">
          <p className="pr-screen__label">Fabric & details</p>
          <div className="pr-grid">
            <label className="pr-field"><span>Fabric type</span><ValidatedInput kind="text" name="fabricType" value={productForm.fabricType} onChange={onChange} /></label>
            <label className="pr-field"><span>Material</span><ValidatedInput kind="text" name="materialFabric" value={productForm.materialFabric} onChange={onChange} /></label>
            <label className="pr-field"><span>Embroidery</span><ValidatedInput kind="text" name="embroidery" value={productForm.embroidery} onChange={onChange} /></label>
            <label className="pr-field"><span>Fit</span><ValidatedInput kind="text" name="fit" value={productForm.fit} onChange={onChange} /></label>
            <label className="pr-field"><span>Care tip</span><ValidatedInput kind="text" name="careTip" value={productForm.careTip} onChange={onChange} /></label>
            <label className="pr-field"><span>Components</span><ValidatedInput kind="text" name="noOfComponents" value={productForm.noOfComponents} onChange={onChange} /></label>
            <label className="pr-field"><span>Colour</span><ValidatedInput kind="text" name="colour" value={productForm.colour} onChange={onChange} /></label>
            <label className="pr-field"><span>Remarks</span><ValidatedInput kind="text" name="remarks" value={productForm.remarks} onChange={onChange} /></label>
          </div>
        </section>

        <section className="pr-block">
          <p className="pr-screen__label">Collections</p>
          <div className="pr-chips">
            {labels.length === 0 && <span className="pr-chips__empty">No collections yet</span>}
            {labels.map((l) => (
              <button
                key={l._id}
                type="button"
                className={`pr-chip${productForm.labels.includes(l._id) ? ' is-on' : ''}`}
                onClick={() => toggleLabel(l._id)}
              >
                {l.name}
              </button>
            ))}
          </div>
        </section>

        <section className="pr-block">
          <p className="pr-screen__label">Media</p>
          <FileDrop files={productFiles} onChange={setProductFiles} existingImages={existingImages} />
          {editingId && (
            <p className="pr-edit-note">Leave empty to keep existing images. New uploads are added (max 7 total).</p>
          )}
        </section>

        <div className="pr-form-actions">
          {editingId && (
            <button type="button" className="pr-cancel" onClick={cancelEdit}>
              Cancel edit
            </button>
          )}
          <button type="submit" className="pr-save" disabled={saving}>
            {saving ? (editingId ? 'Updating…' : 'Creating…') : editingId ? 'Update product' : 'Create product'}
          </button>
        </div>
      </form>

      <aside className="pr-screen__aside">
        <div className="pr-list-section">
          <div className="pr-list-head">
            <p className="pr-screen__label">Active products</p>
            <span>{activeProducts.length}</span>
          </div>
          <div className="pr-list">
            {activeProducts.length === 0 && <p className="pr-empty">No active products.</p>}
            {activeProducts.map((p) => renderProductCard(p))}
          </div>
        </div>

        <div className="pr-list-section pr-list-section--inactive">
          <div className="pr-list-head">
            <p className="pr-screen__label">Deactivated products</p>
            <span>{inactiveProducts.length}</span>
          </div>
          <div className="pr-list">
            {inactiveProducts.length === 0 && (
              <p className="pr-empty">No deactivated products.</p>
            )}
            {inactiveProducts.map((p) => renderProductCard(p, { inactive: true }))}
          </div>
        </div>
      </aside>
    </div>
  );
}
